import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenService } from './token.service';
import { switchMap } from 'rxjs/operators';


export interface User {
  codiceFiscale: string;
  cognome: string;
  nome: string;
  codiceUfficio: string;
  associato?: boolean;
}


export interface UserSearchableDto {
  codiceFiscale?: string | null;
  cognome?: string | null;
  nome?: string | null;
  associato?: boolean;
  enteCode?: string | null;
}


export interface PaginatedUsersResponse {
  content: User[];
  totalElements: number;
}

/*
export interface Attribute {
  attributeId: number;
  attributeName: string;
  attributeValue: string;
  organ: any;
}*/


//Interfaccia per l'oggetto Organ (ufficio)
export interface Organ {
  organId: number;
  nodeCode: string;
  description: string;
  tenant: {
    tenantId: number;
    tenantCode: string;
    description: string;
  };
  //attributes?: Attribute[];
}


//Interfaccia per l'oggetto Application
export interface Application {
  applId: number;
  applCode: string;
  description: string;
}


//Interfaccia per i dati dell'ente da salvare
export interface EnteData {
  codiceFiscale: string;
  provinciaEnte: string;
  tipologiaEnte: string;
  codiceIstat: string;
  descrizione: string;
  indirizzoEnte: string;
  capEnte: string;
  cittaEnte: string;
  faxEnte: string;
  pecEnte: string;
}


//Interfaccia per gli attributi utente da inviare a MyProfile
export interface UserAttributeRequest {
  attributeName: string;
  attributeValue: string;
}



@Injectable({
  providedIn: 'root'
})
export class UsersService {

  constructor(private http: HttpClient, private tokenService: TokenService) { }

  private getAuthHeaders(myProfileToken: string): HttpHeaders {
    return new HttpHeaders({
      'X-MyProfile-Token': myProfileToken || ''
    });
  }

  getUsersByEnte(
    filters: UserSearchableDto,
    sortColumn: string,
    sortDirection: string,
    pageIndex: number,
    pageSize: number
  ): Observable<PaginatedUsersResponse> {


    let params = new HttpParams()
      .set('page', pageIndex.toString())
      .set('size', pageSize.toString())
      .set('sort', `${sortColumn},${sortDirection}`);


    if (filters.codiceFiscale) {
      params = params.set('codiceFiscale', filters.codiceFiscale);
    }
    if (filters.cognome) {
      params = params.set('cognome', filters.cognome);
    }
    if (filters.nome) {
      params = params.set('nome', filters.nome);
    }

    if (filters.associato != null) {
      params = params.set('associato', filters.associato.toString());
    }
    if (filters.enteCode) {
      params = params.set('officeCode', filters.enteCode);
    }


    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = new HttpHeaders({
          'X-MyProfile-Token': myProfileToken || ''
        });

        return this.http.get<PaginatedUsersResponse>(`${environment.urls.base}/gestioneutenti/getUsersByEnte`, { params, headers });
      })
    );
  }


  getUserByCodiceFiscale(codiceFiscale: string): Observable<User> {
    const params = new HttpParams().set('userCode', codiceFiscale);
    return this.http.get<User>(`${environment.urls.base}/gestioneutenti/getUser`, { params });
  }


  /**
   * Esegue la chiamata all'API per creare un nuovo utente.
   * @param userRequest I dati dell'utente da creare.
   */
  createUser(userRequest: { userCode: string, userName: string, userSurname: string, userDomain: string
    /*codiceFiscale: string,
    nome: string,
    cognome: string,
    userDomain: string*/
  }): Observable<any> {
    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = new HttpHeaders({
          'X-MyProfile-Token': myProfileToken || ''
        });

        /*
        const payload = {
          userCode: userRequest.codiceFiscale,
          userDomain: userRequest.userDomain,
          userName: userRequest.nome,
          userSurname: userRequest.cognome,
          userTaxCode: userRequest.codiceFiscale,
        };
        */
        const payload = {
            userCode: userRequest.userCode,
            userDomain: userRequest.userDomain,
            userName: userRequest.userName,
            userSurname: userRequest.userSurname,
            userTaxCode: userRequest.userCode
        };

        return this.http.post(`${environment.urls.base}/gestioneutenti/createUser`, payload, { headers });
      })
    );
  }



  /**
   * Aggiunge una lista di attributi ad un utente già esistente.
   * @param userId L'ID dell'utente.
   * @param attributes La lista degli attributi da associare.
   */
  /*
  addUserAttributes(userId: string, attributes: UserAttributeRequest[]): Observable<any> {
    const requestBody = { userId, attributes };
    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = new HttpHeaders({
          'X-MyProfile-Token': myProfileToken || ''
        });
        return this.http.post(`${environment.urls.base}/gestioneutenti/addUserAttributes`, requestBody, { headers });
      })
    );
  }
  */


  /**
   * Associa un utente a un tenant.
   * @param userId L'ID dell'utente.
   * @param tenantId L'ID del tenant.
   */
  setTenantUser(userId: string, tenantId: number): Observable<any> {
    const requestBody = { userId, tenantId };
    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = new HttpHeaders({
          'X-MyProfile-Token': myProfileToken || ''
        });
        return this.http.post(`${environment.urls.base}/gestioneutenti/setTenantUser`, requestBody, { headers });
      })
    );
  }


  /**
   * Associa un utente a un'applicazione.
   * @param userId L'ID dell'utente.
   * @param tenantId L'ID del tenant.
   * @param appId L'ID dell'applicazione.
   */
  setTenantUserApp(userId: string, tenantId: number, appId: number): Observable<any> {
    const requestBody = { userId, tenantId, appId };
    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = new HttpHeaders({
          'X-MyProfile-Token': myProfileToken || ''
        });
        return this.http.post(`${environment.urls.base}/gestioneutenti/setTenantUserApp`, requestBody, { headers });
      })
    );
  }


  /**
   * Associa un utente a un ufficio (ente).
   * @param userCode Il codice fiscale dell'utente.
   * @param userId L'ID dell'utente restituito dalla chiamata createUser.
   * @param tenantId L'ID del tenant.
   * @param officesId L'elenco degli ID degli uffici da associare.
   */
  setTenantUserOffices(userCode: string, userId: string, tenantId: number, officesId: number[]): Observable<any> {
    const requestBody = {
      userCode,
      userId,
      tenantId,
      officesId
    };
    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = new HttpHeaders({
          'X-MyProfile-Token': myProfileToken || ''
        });
        return this.http.post(`${environment.urls.base}/gestioneutenti/setTenantUserOffices`, requestBody, { headers });
      })
    );
  }


  //Recupera la lista degli uffici con i loro ID numerici
  getTenantOffices(tenantCode?: string): Observable<Organ[]> {
    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = this.getAuthHeaders(myProfileToken);
        let params = new HttpParams();

        if (tenantCode) {
          params = params.set('tenantCode', tenantCode);
        }

        return this.http.get<Organ[]>(`${environment.urls.base}/gestioneutenti/getTenantOffices`, { headers, params });
      })
    );
  }


  //Recupera la lista delle applicazioni
  getApplications(): Observable<Application[]> {
    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = this.getAuthHeaders(myProfileToken);
        return this.http.get<Application[]>(`${environment.urls.base}/gestioneutenti/getApplications`, { headers });
      })
    );
  }


  deleteUser(userCode: string, officeCode: string): Observable<any> {
    const params = new HttpParams()
      .set('userCode', userCode)
      .set('officeCode', officeCode);

    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = new HttpHeaders({
          'X-MyProfile-Token': myProfileToken || ''
        });
        return this.http.delete(`${environment.urls.base}/gestioneutenti/removeUserOffice`, { params, headers });
      })
    );
  }


  /**
  * Recupera l'Organ specifico (con i suoi attributi) tramite il nodeCode.
  * Corrisponde all'endpoint Backend /getOrganAttributes.
  */
 /*
  getOrganByNodeCode(nodeCode: string): Observable<Organ> {
    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = this.getAuthHeaders(myProfileToken);
        // Passa il nodeCode come parametro URL
        const params = new HttpParams().set('nodeCode', nodeCode);

        return this.http.get<Organ>(`${environment.urls.base}/gestioneutenti/getOrganAttributes`, { headers, params });
      })
    );
  }
  */


  /**
  * Associa il ruolo EELL all'utente appena creato.
  * Corrisponde all'endpoint Backend /addUserRoleEELL.
  * @param userId L'ID numerico dell'utente.
  * @param tenantId L'ID numerico del tenant.
  */
  addUserRoleEELL(userId: number, tenantId: number, appId: number): Observable<any> {
    const requestBody = {
      userId: userId.toString(),
      tenantId: tenantId.toString(),
      appId: appId.toString()
    };

    return this.tokenService.getJwtMyProfile().pipe(
      switchMap(myProfileToken => {
        const headers = new HttpHeaders({
          'X-MyProfile-Token': myProfileToken || ''
        });
        return this.http.post(`${environment.urls.base}/gestioneutenti/addUserRoleEELL`, requestBody, { headers });
      })
    );
  }

}
